<template>
    <div class="chat-container" :class="{ 'other': type == 'other' }">
        <div class="notes">
            <div v-for="comment in sortedComments" :key="comment.id">
                <Message :comment="comment" :type="type" :commentId="uploadingCommentId" />
            </div>
        </div>
        <div class="reply" v-if="editor">      
            <div class="text">
                <div class="icon">
                    {{ currentUser.name.charAt(0).toUpperCase() }}
                </div>
                <div class="editor-container">
                    <editor-content :editor="editor" class="editor" />
                </div>
                <button @click="sendComment">Enviar</button>
            </div>
            <div class="image-preview">
                <div class="image" v-for="(preview, index) in imagePreviews" :key="index">
                    <div class="remove-image" @click="removeImage(index)">
                        <img src="../../../../public/img/icons/status-5.png" alt="">
                    </div>
                    <div class="image-container">
                        <img v-if="!isPdf(preview)" :src="preview.url" alt="">
                        <img v-else class="file" src="../../../../public/img/file_link.png" alt="" @click="openPdf(preview)">
                    </div>
                    <div class="image-description">
                        <span class="name">{{ getFileName(preview) }}</span>
                        <span class="extension">{{ getExtension(preview) }}</span>
                    </div>
                </div>
            </div>
            <div class="styles">
                <div class="image">
                    <input
                        type="file"
                        style="display: none"
                        ref="image"
                        accept="*"
                        multiple
                        @change="handelImage"   
                    />                    
                    <img src="../../../../public/img/icons/clip.png" alt="" @click="openFileInput" style="cursor: pointer;">
                </div>
                <span
                    @click="setLink"
                    class="opt link"
                    :class="{ 'is-active': editor.isActive('link') }"
                >
                </span>
                <div class="editor-actions">
                    <span
                        @click="editor.chain().focus().toggleBold().run()"
                        class="opt bold"
                        :class="{ 'is-active': editor.isActive('bold') }"
                    >
                    </span>
                    <span
                        @click="editor.chain().focus().toggleUnderline().run()"
                        class="opt underline"
                        :class="{ 'is-active': editor.isActive('underline') }"
                    >
                    </span>
                    <span
                        @click="editor.chain().focus().toggleBulletList().run()"
                        class="opt bullet-list"
                        :class="{ 'is-active': editor.isActive('bulletList') }"
                    >
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Message from './Message.vue';
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import BulletList from '@tiptap/extension-bullet-list';
import i18n from '@/i18n';

export default {
    name: 'chat',
    components: {
        Message,
        EditorContent
    },
    props: {
        order_id: String,
        type: String,
        autoLoad: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            editor: null,
            linkUrl: '',
            images: [],
            remoteUrl: '',
            preview: false,
            previewImage: '',
            uploadingCommentId: null
        }
    },
    computed: {
        comments() {
            return this.$store.getters['orders/getComments'](this.order_id);
        },
        sortedComments() {
            return [...this.comments].sort((a, b) => new Date(a.create_date) - new Date(b.create_date));
        },
        currentUser() {
            return this.$store.getters['user/getUser'];
        },
        imagePreviews() {
            return this.images.map(image => ({
                url: URL.createObjectURL(image.file),
                file: image.file,
                name: image.name
            }));
        }
    },
    methods: {
        loadComments() {
            this.$store.dispatch('orders/loadComments', this.order_id).then(() => {
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
            });
        },
        scrollToBottom() {
            const notesContainer = this.$el.querySelector('.notes');
            if (notesContainer) {
                notesContainer.scrollTop = notesContainer.scrollHeight;
            }
        },
        sendComment() {
            const content = this.editor.getHTML();
            const plainText = this.editor.getText();

            if (plainText.trim()) {
                this.$store.dispatch('orders/addComment', {
                    order_id: this.order_id,
                    user_id: this.currentUser.id,
                    message: content,
                }).then((response) => {
                    this.editor.commands.clearContent();
                    this.loadComments();

                    if (this.images.length > 0) {
                        this.uploadingCommentId = response.id;

                        this.$store.dispatch('orders/uploadImages', {
                            id: response.id,
                            images: this.images
                        }).then(() => {
                            this.uploadingCommentId = null;
                            this.loadComments();
                        });
                    }
                    this.removeAllImages();
                });
            }
        },
        updatePlaceholder() {
            const editorElement = this.$el.querySelector('.ProseMirror');
            if (editorElement) {
                const isEmpty = this.editor.isEmpty;
                editorElement.classList.toggle('is-editor-empty', isEmpty);
            }
        },
        setLink() {
            const previousUrl = this.editor.getAttributes('link').href;
            const url = window.prompt('URL', previousUrl);

            if (url === null) {
                return;
            }

            if (url === '') {
                this.editor.chain().focus().unsetLink().run();
                return;
            }

            this.editor.chain().focus().setLink({ href: url }).run();
        },
        handelImage(event) {
            const files = event.target.files;
            this.images = [];
            this.imageError = '';

            if (files.length > 5) {
                this.imageError = this.$i18n.t('views.order.error.image');
                return;
            }

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                this.images.push({
                    file: file,
                    name: file.name
                });
            }
        },
        openFileInput() {
            this.$refs.image.click();
        },
        setUnreadMessages() {
            this.$store.dispatch('orders/setReadMessages', {
                orderId: this.order_id,
                userId: this.currentUser.id,
            });
        },
        removeImage(index) {
            this.images.splice(index, 1);
            URL.revokeObjectURL(this.imagePreviews[index]);
        },
        removeAllImages() {
            this.imagePreviews.forEach(image => {
                URL.revokeObjectURL(image);
            });
            this.images = [];
        },
        openPreview(preview) {
            this.preview = true;
            this.previewImage = preview;
        },
        isPdf(preview) {
            if (!preview || !preview.file) {
                return false;
            }
            const path = (preview.name || '').split('?')[0].toLowerCase();
            const ext = path.split('.').pop();
            if (ext === 'pdf' || ext === 'zip') {
                return true;
            }
            const mime = preview.file.type || '';
            return (
                mime === 'application/pdf' ||
                mime === 'application/zip' ||
                mime === 'application/x-zip-compressed' ||
                mime === 'application/x-zip'
            );
        },
        openPdf(preview) {
            if (preview && preview.url) {
                window.open(preview.url, '_blank', 'noopener,noreferrer');
            }
        },
        getFileName(url) {
            return url.name.replace(/\.[^/.]+$/, '');
        },
        getExtension(url) {
            return url.name.split('.').pop().toUpperCase();
        }
    },
    watch: {
        comments: {
            handler() {
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
            },
            deep: true
        },
        'editor.isEmpty': {
            handler(isEmpty) {
                this.$nextTick(() => {
                    this.updatePlaceholder();
                });
            },
            immediate: true
        }
    },
    mounted() {
        this.editor = new Editor({
            extensions: [
                StarterKit,
                Underline,
                Link.configure({
                    openOnClick: false,
                    HTMLAttributes: {
                        rel: 'noopener noreferrer',
                        target: '_blank',
                    },
                }),
                BulletList,
                Placeholder.configure({
                    placeholder: this.$i18n.t('general.comment'),
                    emptyEditorClass: 'is-editor-empty',
                    showOnlyWhenEditable: true,
                    showOnlyCurrent: true
                })
            ],
            content: '',
            onUpdate: () => {
                this.$nextTick(() => {
                    this.updatePlaceholder();
                });
            },
            editorProps: {
                handlePaste: (view, event) => {
                    return false;
                }
            }
        });

        // Force an update after initialization
        this.$nextTick(() => {
            this.updatePlaceholder();
        });

        if (this.autoLoad) {
            this.loadComments();
        }

        this.setUnreadMessages();
    },
    beforeDestroy() {
        this.editor.destroy();
    }
}
</script>

<style lang="scss" scoped>
.chat-container {
    width: 100%;
    height: 500px;
    padding: 0px;
    margin: 0px auto;
    margin-top: 24px;
    float: left;
    background-color: #FFFFFF;
    border: 1px solid #CCCCCC;
    border-radius: 4px;
    position: relative;
    display: flex;
    flex-direction: column;

    &.other {
        margin-top: 0px;
    }
    
    .notes {
        width: calc(100% - 32px);
        flex: 1;
        margin: 16px;
        margin-left: 24px;
        overflow-y: auto;
        padding-right: 8px;
        min-height: 0;
        max-height: calc(100% - 153px);
    }

    .reply {
        width: calc(100% - 32px);
        min-height: 105px;
        margin: 16px;
        margin-top: auto;
        border: 1px solid #CCCCCC;
        border-radius: 4px;

        .text {
            width: 100%;
            min-height: 65px;
            display: flex;
            flex-direction: row;
            justify-content: left;
            position: relative;

            .icon {
                flex-shrink: 0;
                width: 33px;
                height: 33px;
                margin: 16px;
                border-radius: 99px;
                background-color: #222222;
                color: #FFFFFF;
                font-size: 14px;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 0;
                line-height: 1;
            }

            .editor-container {
                flex: 1;
                min-width: 0;
                height: auto;
                margin: auto;
                position: relative;

                .editor {
                    width: 100%;
                    min-height: 33px;
                    padding: 16px 8px;
                    font-size: 16px;
                    color: #2C3E50;
                    outline: none;
                    border: none;
                }
            }

            button {
                flex-shrink: 0;
                height: 33px;
                width: 73px;
                background-color: #FFBB0C;
                border: none;
                outline: none;
                color: #222222;
                margin: 16px;
                border-radius: 4px;
                align-self: flex-end;
            }
        }
        .image-preview {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: center;
            justify-content: left;
            gap: 8px;
            margin: 8px;

            .image {
                width: 176px;
                height: 88px;
                display: flex;
                flex-shrink: 0;
                padding: 4px;
                align-items: center;
                gap: 8px;
                border-radius: 8px;
                border: 1px solid #222222;
                background: #FFFFFF;
                position: relative;

                .remove-image {
                    width: 24px;
                    height: 24px;
                    cursor: pointer;
                    position: absolute;
                    padding: 0 4px;
                    z-index: 1000;
                    background-color: black;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    top: -12px;
                    right: -12px;
                    border-radius: 999px;

                    img {
                        width: 16px;
                        height: 16px;
                        filter: invert(1);
                    }
                }

                .image-container {
                    width: 80px;
                    height: 80px;
                    object-fit: cover;
                    display: flex;
                    justify-content: center;
                    align-items: center;

                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }

                    .file {
                        width: 36px;
                        height: 36px;
                    }

                    .loader {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        filter: invert(1);
                    }
                }

                .image-description {
                    font-size: 12px;
                    color: #2C3E50;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    flex: 1 0 0;
                    align-self: stretch;

                    .name {
                        width: 80px;
                        height: 80px;
                        overflow: hidden;
                        text-align: center;
                        color: #2C3E50;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        font-size: 12px;
                        font-style: normal;
                        font-weight: 400;
                        line-height: normal;
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                        white-space: normal;
                        display: flex;
                        align-items: center;
                        word-break: break-word;

                    }

                    .extension {
                        width: 80px;
                        height: 26px;
                        overflow: hidden;
                        color: #757575;
                        text-align: left;
                        text-overflow: ellipsis;
                        font-size: 12px;
                        font-style: normal;
                        font-weight: 400;
                        line-height: normal;
                        display: flex;
                        align-items: center;
                    }
                }
            }
        }
        
        .styles {
            width: 100%;
            height: 40px;
            background-color: #DDDDDD;
            display: flex;
            flex-direction: row;
            align-items: center;
            padding-left: 8px;

            .opt {
                width: 24px;
                height: 24px;
                cursor: pointer;
                border-radius: 4px;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-right: 8px;

                &.link {
                    background-image: url('../../../../public/img/icons/link.png');
                    background-repeat: no-repeat;
                    background-position: center;
                }
            }

            .editor-actions {
                display: flex;
                flex-direction: row;
                align-items: center;
                border-left: 1px solid #999999;

                .opt {
                    width: 24px;
                    height: 24px;
                    cursor: pointer;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-left: 8px;
                    margin-right: 0px;

                    &.is-active {
                        background-color: #e0e0e0;
                    }

                    &.bold {
                        font-weight: bold;
                        background-image: url('../../../../public/img/icons/bold.png');
                        background-repeat: no-repeat;
                        background-position: center;
                    }

                    &.underline {
                        height: 24px;
                        width: 24px;
                        text-decoration: underline;
                        background-image: url('../../../../public/img/icons/underline.png');
                        background-size: 20px;
                        background-repeat: no-repeat;
                        background-position: center;
                    }

                    &.bullet-list {
                        background-image: url('../../../../public/img/icons/bullet-list.png');
                        background-repeat: no-repeat;
                        background-position: center;
                    }
                }
            }

            .image {
                height: 24px;
                width: 24px;
                margin-right: 8px;

                input {
                    display: none;
                }
                img {
                    height: 24px;
                    width: 24px;
                }
            }
        }
    }
}
</style>

<style lang="scss">
.ProseMirror {
    width: 100%;
    min-height: 20px;
    max-height: 200px;
    overflow-y: auto;
    outline: none;
    padding: 0;
    margin: 0;
    position: relative;
    display: block;
    
    p {
        margin: 0;
        padding: 0;
        line-height: 1.2;
        width: 100%;
        word-wrap: break-word;
        white-space: pre-wrap;
    }

    p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        color: #999999;
        pointer-events: none;
        position: absolute;
        font-size: 16px;
        opacity: 1;
        width: 100%;
        height: 100%;
        background: transparent;
        float: left;
    }
}
</style>